/* Traer TODAS las filas de una consulta, y no las primeras mil.
 *
 * ── EL ERROR QUE ESTO EXISTE PARA QUE NO VUELVA A PASAR ─────────────────────
 *
 * PostgREST corta la respuesta en 1.000 filas. No avisa: no es un error, no es
 * un encabezado que rompa nada, es una respuesta HTTP 200 con menos datos. El
 * tablero de Presidencia pidió 1.659 filas ordenadas por fecha, recibió 1.000,
 * y **mayo apareció con 75 viajes y ningún bitrén** — el mes real tiene 636 y
 * 27. Los datos se cortaban el 06/05 y de ahí en adelante no había nada.
 *
 * Lo peor no es el número mal. Es que un mes incompleto **se ve igual que un
 * mes flojo**: las tablas se dibujan, las semanas están, los porcentajes
 * cierran en 100 porque cierran sobre lo que llegó. No hay ninguna señal.
 *
 * Y `.limit(20000)` NO alcanza — que es lo que tenía. El límite del cliente no
 * levanta el techo del servidor: se aplica el más chico de los dos, y el del
 * servidor gana siempre.
 *
 * ── POR QUÉ SE EXIGE `count: "exact"` ───────────────────────────────────────
 *
 * La forma obvia de paginar es «pedir de a mil hasta que venga una página con
 * menos de mil». Es la que parece bien y es la que falla callada: si el techo
 * del servidor fuera 500, la primera página vendría con 500, el bucle lo
 * leería como «se terminaron los datos» y cortaría en 500 — el mismo error de
 * hoy, con otro número.
 *
 * Por eso acá no se deduce nada: se le pregunta a la base **cuántas hay**, se
 * pagina hasta tenerlas todas, y si no se llega **se tira un error**. Una
 * pantalla rota se arregla; una pantalla que miente, no — porque nadie se
 * entera de que hay algo que arreglar.
 *
 * ── EL ORDEN TIENE QUE SER ÚNICO ────────────────────────────────────────────
 *
 * Paginar con un orden que empata es paginar mal: dos filas con la misma fecha
 * pueden salir en distinto puesto en dos pedidos, y entonces una se repite en
 * la página 2 y otra no sale nunca. **Ordenar por algo que no se repita** — si
 * no hay una columna única, varias que juntas lo sean.
 *
 * ── CÓMO SE USA ─────────────────────────────────────────────────────────────
 *
 *     const filas = await traerTodas(() =>
 *       supabase
 *         .from("presidencia_resumen")
 *         .select("fecha_salida,cliente,viajes", { count: "exact" })
 *         .order("fecha_salida")
 *         .order("cliente"));
 *
 * Se pasa una FUNCIÓN que arma la consulta, no la consulta: cada página es un
 * pedido nuevo y un builder de supabase-js no se puede reusar.
 */

/* El techo de PostgREST. Se pide de a esto y no de a más porque pedir de más
   no sirve: el servidor recorta igual. */
export const PAGINA = 1000;

/* Un tope de páginas para que un bug no se convierta en un bucle infinito
   contra la base. 200 páginas son 200.000 filas: muy por encima de cualquier
   pantalla del hub, y muy por debajo de colgar el navegador. */
export const TOPE_DE_PAGINAS = 200;

export async function traerTodas(armarConsulta, opciones = {}) {
  const { pagina = PAGINA, topeDePaginas = TOPE_DE_PAGINAS } = opciones;
  const filas = [];
  let esperadas = null;

  for (let vuelta = 0; vuelta < topeDePaginas; vuelta++) {
    const desde = filas.length;
    const { data, error, count } = await armarConsulta().range(
      desde,
      desde + pagina - 1,
    );
    if (error) throw error;

    if (esperadas === null) {
      if (typeof count !== "number") {
        // No es un detalle que se pueda dejar pasar: sin el total no hay forma
        // de saber si faltan filas, que es lo único que esto hace.
        throw new Error(
          'traerTodas necesita el total: agregá { count: "exact" } al .select()',
        );
      }
      esperadas = count;
    }

    filas.push(...(data || []));

    if (filas.length >= esperadas) return filas;

    // Faltan filas y la última página vino vacía: seguir sería girar en falso.
    if (!data || data.length === 0) {
      throw new Error(
        `Faltan filas: llegaron ${filas.length} de ${esperadas} y la base dejó de mandar`,
      );
    }
  }

  throw new Error(
    `Se pasó de ${topeDePaginas} páginas trayendo ${esperadas} filas: revisá el orden de la consulta`,
  );
}
